/*  eslint-disable @typescript-eslint/unbound-method */
import { type Container, type FederatedPointerEvent, Point } from 'pixi.js';
import { Signal } from 'typed-signals';

export interface DragTarget {
  readonly container: Container;
  readonly onRelease?: (target: Container) => void;
  readonly onUpdate?: (globalX: number, globalY: number) => void;
  readonly allowRelease?: () => boolean;
}

interface InteractiveState {
  interactive: boolean;
  interactiveChildren: boolean;
}

/**
 * Class for managing dragging of PIXI containers
 */
export class DragController {
  private static readonly offsetMovementDivisor = 5;

  private readonly stage: Container;

  private dragTarget: DragTarget | null = null;

  private readonly globalPointer = new Point();

  private readonly parentPointer = new Point();

  private readonly dragOffset = new Point();

  private readonly dragOffsetGoal = new Point();

  public useClickOffset = true;

  private activeTouchId = -1;

  public readonly onDragStarted = new Signal<(container: Container) => void>();

  private readonly interactiveState: InteractiveState = {
    interactive: true,
    interactiveChildren: false,
  };

  constructor(stage: Container) {
    this.stage = stage;
    stage.on('globalpointermove', this.onPointerMove, this);
    stage.on('touchstart', this.onTouchStart, this);
    stage.on('touchend', this.onTouchEnd, this);

    document.addEventListener('touchend', this.checkIfAnyTouchIsStillActive);
    document.addEventListener('touchcancel', this.checkIfAnyTouchIsStillActive);
  }

  public dispose(): void {
    this.activeTouchId = -1;
    this.stage.removeListener('globalpointermove', this.onPointerMove, this);
    this.stage.removeListener('touchstart', this.onTouchStart, this);
    document.removeEventListener('touchcancel', this.checkIfAnyTouchIsStillActive);
    document.removeEventListener('touchend', this.checkIfAnyTouchIsStillActive);
    this.onDragStarted.disconnectAll();
  }

  private readonly checkIfAnyTouchIsStillActive = (ev: TouchEvent): void => {
    if (Object.values(ev.targetTouches).length > 0) return;
    this.activeTouchId = -1;
  };

  private onTouchStart(e: FederatedPointerEvent): void {
    if (this.touchActive) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    this.activeTouchId = e.pointerId;
    this.onPointerMove(e);
  }

  private onTouchEnd(e: FederatedPointerEvent): void {
    if (this.activeTouchId === e.pointerId) {
      this.activeTouchId = -1;
    } else {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  private onPointerMove(e: FederatedPointerEvent): void {
    if (this.touchActive && this.activeTouchId !== e.pointerId) return;
    this.globalPointer.set(e.globalX, e.globalY);
  }

  public update(deltaTime: number): void {
    const { dragTarget } = this;
    if (!dragTarget) return;

    const { globalPointer, parentPointer, dragOffset, dragOffsetGoal } = this;
    const { container, onUpdate } = dragTarget;

    if (!dragOffset.equals(dragOffsetGoal)) {
      const movementDivisor = DragController.offsetMovementDivisor;
      const deltaX = ((dragOffsetGoal.x - dragOffset.x) / movementDivisor) * deltaTime;
      const deltaY = ((dragOffsetGoal.y - dragOffset.y) / movementDivisor) * deltaTime;
      dragOffset.x = Math.abs(deltaX) > 1 ? dragOffset.x + deltaX : dragOffset.x;
      dragOffset.y = Math.abs(deltaY) > 1 ? dragOffset.y + deltaY : dragOffset.y;
    }

    container.parent?.toLocal(globalPointer, undefined, parentPointer, false);
    container.x = parentPointer.x - dragOffset.x;
    container.y = parentPointer.y - dragOffset.y;

    if (onUpdate) onUpdate(globalPointer.x, globalPointer.y);
  }

  private onPointerReleased(): void {
    if (!this.dragTarget) return;

    const { container, onRelease, allowRelease } = this.dragTarget;

    if (!(allowRelease?.() ?? true)) return;

    this.cancelDrag();

    if (onRelease) onRelease(container);
  }

  public cancelDrag(): void {
    if (!this.dragTarget) return;

    const { stage, interactiveState, dragOffset } = this;
    const { container } = this.dragTarget;

    stage.removeListener('pointerup', this.onPointerReleased);
    stage.removeListener('pointerupoutside', this.onPointerReleased);

    // Restores the previous interactive state.
    container.interactive = interactiveState.interactive;
    container.interactiveChildren = interactiveState.interactiveChildren;

    dragOffset.set(0, 0);

    this.dragTarget = null;
  }

  public startDrag(dragTarget: DragTarget): void {
    const { container } = dragTarget;
    if (this.touchActive || !container.parent) return;

    const { stage, interactiveState, useClickOffset, dragOffset, dragOffsetGoal, globalPointer } = this;

    this.dragTarget = dragTarget;

    // Capture interactive state, and set the container interactive = false.
    interactiveState.interactive = container.interactive ?? false;
    interactiveState.interactiveChildren = container.interactiveChildren ?? false;
    container.interactive = false;
    container.interactiveChildren = false;

    if (useClickOffset && container.parent) {
      container.parent.toLocal(globalPointer, undefined, dragOffset);
      dragOffset.set(dragOffset.x - container.x, dragOffset.y - container.y);
    } else {
      dragOffset.set(0, 0);
    }
    dragOffsetGoal.set(dragOffset.x, dragOffset.y);

    stage.on('pointerup', this.onPointerReleased, this);
    stage.on('pointerupoutside', this.onPointerReleased, this);

    this.update(0);

    this.onDragStarted.emit(container);
  }

  public setDragOffset(offsetX: number, offsetY: number): void {
    const { dragOffset, dragOffsetGoal } = this;
    dragOffsetGoal.set(dragOffset.x + offsetX, dragOffset.y + offsetY);
  }

  get touchActive(): boolean {
    return this.activeTouchId >= 0;
  }

  public windowFocusChange(): void {
    this.onPointerReleased();
  }

}
